import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play } from 'lucide-react';
import { AudioPlayer, Button } from '@ai-tutor/ui';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';
import { useOptionalPodcastPlayer } from '@/contexts/PodcastPlayerContext';
import {
  isPodcastInFlight,
  toTrack,
  useGenerateSectionPodcast,
  useSectionPodcast,
  useSectionPodcastSummary,
} from '@/hooks/lesson/useSectionPodcasts';
import { PodcastSkeleton } from '../skeletons/PanelSkeletons';
import { PodcastTranscript } from '../media/PodcastTranscript';
import { PodcastLayout, toUiTranscript } from './PodcastLayout';

interface SectionPodcastViewProps {
  lessonId: string;
  lessonTitle: string;
  sectionIndex: number;
  total: number;
  onBack: () => void;
  onProgressUpdate?: (mediaType: string, progress: number) => void;
}

/** Back to the list of section podcasts, and where this one sits. */
function EpisodeTop({
  index,
  total,
  onBack,
}: {
  index: number;
  total: number;
  onBack: () => void;
}) {
  return (
    <div className='mb-6 flex items-center gap-3'>
      <Button
        variant='ghost'
        size='sm'
        onClick={onBack}
        className='tap-target -ml-2 h-8 gap-1.5 px-2 text-muted-foreground'
      >
        <ArrowLeft className='h-4 w-4' aria-hidden />
        All podcasts
      </Button>
      <span className='ml-auto font-mono text-xs tabular-nums text-muted-foreground/90'>
        Section {index} of {total}
      </span>
    </div>
  );
}

type Notice = [title: string, description: string, action: string];

const NOT_MADE: Notice = [
  'No podcast for this section yet',
  'Two hosts talk this section through. It takes about a minute.',
  'Make podcast',
];

/** What to say instead of the player, or null when it can play. */
function noticeFor(
  status: string | undefined,
  locked: boolean,
  hasAudio: boolean
): Notice | null {
  if (status === 'failed') {
    return [
      "This section's podcast didn't finish",
      'Something went wrong while recording it.',
      'Try again',
    ];
  }
  if (status !== 'completed') {
    return locked
      ? [
          'A podcast for every section is a Plus feature',
          "On Free, a lesson's first section gets a podcast.",
          'See Plus',
        ]
      : NOT_MADE;
  }
  // Completed, but its audio link didn't mint: making it again is the fix
  // when the file is gone.
  return hasAudio
    ? null
    : [
        "Couldn't load the podcast audio",
        "The podcast is ready, but its audio file didn't load. Make it again if this keeps happening.",
        'Make it again',
      ];
}

/**
 * One section's podcast: its player and karaoke transcript. The player is
 * the app's one audio element (`PodcastPlayerContext`), so what plays here
 * is what the Navbar pill shows and keeps playing after you leave. While
 * another section is playing, this one waits for "Play this section"
 * rather than cutting it off.
 */
export function SectionPodcastView(props: SectionPodcastViewProps) {
  const { lessonId, sectionIndex, onBack } = props;
  const podcast = useSectionPodcast(lessonId, sectionIndex);
  const summary = useSectionPodcastSummary(lessonId, sectionIndex);
  const data = podcast.data;
  const status = data?.status ?? summary?.status;
  const top = (
    <div className='px-6 pt-8 sm:px-10'>
      <div className='mx-auto w-full max-w-[68ch]'>
        <EpisodeTop index={sectionIndex} total={props.total} onBack={onBack} />
      </div>
    </div>
  );

  if (!data || isPodcastInFlight(status)) {
    return (
      <>
        {top}
        <PodcastSkeleton
          mode={podcast.isLoading ? 'fetching' : 'generating'}
          pending={status === 'pending'}
        />
      </>
    );
  }
  const track = toTrack(lessonId, props.lessonTitle, data);
  const notice = noticeFor(status, !!summary?.locked, !!track);
  if (!notice && track) return <EpisodePlayer {...props} track={track} />;
  return (
    <>
      {top}
      <EpisodeNotice
        notice={notice ?? NOT_MADE}
        lessonId={lessonId}
        sectionIndex={sectionIndex}
        locked={!!summary?.locked}
        onBack={onBack}
      />
    </>
  );
}

/** Instead of the player: why, and the one thing to do about it. */
function EpisodeNotice({
  notice: [title, description, action],
  lessonId,
  sectionIndex,
  locked,
  onBack,
}: {
  notice: Notice;
  lessonId: string;
  sectionIndex: number;
  locked: boolean;
  onBack: () => void;
}) {
  const generate = useGenerateSectionPodcast(lessonId);
  const navigate = useNavigate();
  return (
    <PanelFallback
      variant='step'
      title={title}
      description={description}
      primary={{
        label: action,
        onClick: () =>
          locked
            ? navigate('/settings?tab=payment')
            : generate.mutate(sectionIndex),
        busy: generate.isPending,
      }}
      secondary={{ label: 'All podcasts', onClick: onBack }}
    />
  );
}

function EpisodePlayer({
  lessonId,
  sectionIndex,
  total,
  onBack,
  onProgressUpdate,
  track,
}: SectionPodcastViewProps & {
  track: NonNullable<ReturnType<typeof toTrack>>;
}) {
  const player = useOptionalPodcastPlayer();
  const { data } = useSectionPodcast(lessonId, sectionIndex);
  const transcript = useMemo(
    () => toUiTranscript(data?.transcript ?? []),
    [data?.transcript]
  );
  const attached = !!player?.isCurrent(lessonId, sectionIndex);

  // Make this section the player's, unless another one is playing - once
  // per section opened, so a refetch's fresh link never reloads it.
  const cued = useRef<string | null>(null);
  useEffect(() => {
    const key = `${lessonId}:${sectionIndex}`;
    if (!player || cued.current === key) return;
    cued.current = key;
    player.cue(track);
  }, [player, track, lessonId, sectionIndex]);

  const currentTime = attached ? (player?.currentTime ?? 0) : 0;
  useEffect(() => {
    if (attached && track.duration > 0) {
      onProgressUpdate?.('audio', (currentTime / track.duration) * 100);
    }
  }, [attached, currentTime, track.duration, onProgressUpdate]);

  return (
    <PodcastLayout
      title={track.sectionTitle}
      duration={track.duration}
      meta={`Podcast · Section ${sectionIndex}`}
      top={<EpisodeTop index={sectionIndex} total={total} onBack={onBack} />}
    >
      {attached && player?.media && player.track ? (
        <AudioPlayer
          media={player.media}
          url={player.track.audioUrl}
          peaks={player.track.peaks}
          duration={player.track.duration}
        />
      ) : (
        <div className='flex flex-wrap items-center gap-3 rounded-xl border border-foreground/10 p-4'>
          <p className='min-w-0 flex-1 text-sm text-muted-foreground'>
            {player?.track
              ? `Section ${player.track.sectionIndex} is playing.`
              : 'Ready to play.'}
          </p>
          <Button size='sm' onClick={() => player?.play(track)}>
            <Play className='mr-1.5 h-4 w-4' aria-hidden='true' />
            Play this section
          </Button>
        </div>
      )}
      <PodcastTranscript
        transcript={transcript}
        currentTime={currentTime}
        onSeek={t => (attached ? player?.seek(t) : undefined)}
      />
    </PodcastLayout>
  );
}
